import { Component, Vue } from 'vue-property-decorator';
import { uniqBy, startCase } from 'lodash';
import confetti from 'canvas-confetti';
import { CONFETTI_COLORS, REWARD_PERIOD_STATUSES, REWARD_REQUEST_STATUSES } from '@/constants';
import {
  SelectionOptions,
  RewardItemDisplayDTO,
  RewardRequestDTO,
  UserDTO,
  RewardPeriodDTO,
  RewardItemDTO,
  UserRewardRequestInfoDTO
} from '@/models';
import { RewardDTO } from '@/models/reward';
import {
  fetchUserRedeemableDrops,
  fetchRewardPeriods,
  fetchRewardRequestByUserId,
  fetchCurrentUserRewardRequestInfo
} from '@/services';
import { sortArrayByDateField } from '@/helpers';
import RewardsHistory from '@/components/pages/rewards/reward-history/RewardsHistory.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import PrizeCard from '@/components/pages/rewards/prize-card/PrizeCard.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import ShippingInfoDialog from './shipping-info-dialog/ShippingInfoDialog.vue';

@Component({
  components: {
    RewardsHistory,
    Pagination,
    PrizeCard,
    FilterDropdown,
    SearchInput,
    InlineLoader,
    Tooltip,
    ShippingInfoDialog
  }
})
export default class Rewards extends Vue {
  private rewardItems: RewardItemDisplayDTO[] = [];
  private exchangeRewardsHistory: RewardRequestDTO[] = [];
  private redeemableDrops: number;
  private searchName = '';
  private prizeTypeOptions: SelectionOptions = [];
  private selectedPrizeTypes = [] as string[];
  private sortBy = '';
  private sortDirection = 0;
  private isUnavailablePrizesHidden = false;
  private sortByOptions: SelectionOptions = [];
  private publishedRewardPeriod: RewardPeriodDTO = {} as RewardPeriodDTO;
  private pageCount = 0;
  private page = 1;
  private itemsPerPage = 15;
  private currentUser: UserDTO = {} as UserDTO;
  private isFetchingRedeemable = true;
  private isFetchingPrizes = true;
  private isFetchingHistory = true;
  private userRewardRequestInfo: UserRewardRequestInfoDTO = {} as UserRewardRequestInfoDTO;
  private shippingInfoDialogShown = false;

  setInitialPrizeTypesOption(): void {
    uniqBy(this.rewardItems, 'type').forEach(item => {
      this.prizeTypeOptions.push({ text: startCase(item.type.toLowerCase()), value: item.type });
    });
    this.prizeTypeOptions.sort((type1, type2) => type1.text.localeCompare(type2.text));
  }

  setInitialSortByOptions(): void {
    this.sortByOptions = [
      {
        text: 'Name',
        value: 'name'
      },
      {
        text: 'Cost',
        value: 'cost'
      },
      {
        text: 'Items left',
        value: 'quantity'
      }
    ];
    this.sortBy = 'name';
  }

  onPrizeTypeChange(selectedPrizeTypes: string[]): void {
    this.selectedPrizeTypes = selectedPrizeTypes;
  }

  onSearch(searchText: string): void {
    this.searchName = searchText;
  }

  showConfetti(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      confetti({
        angle: 125,
        colors: CONFETTI_COLORS,
        spread: 100,
        particleCount: 140,
        origin: { x: 1, y: 1 }
      });
    }, 500);
    setTimeout(() => {
      confetti({
        angle: 125,
        colors: CONFETTI_COLORS,
        spread: 100,
        particleCount: 140,
        origin: { x: 1, y: 1 }
      });
    }, 800);
  }

  async setPublishedRewardPeriod(): Promise<void> {
    const { data } = await fetchRewardPeriods();
    const publishedRewardPeriod = data.find(item => item.status === REWARD_PERIOD_STATUSES.PUBLISHING.label);
    if (publishedRewardPeriod) {
      this.publishedRewardPeriod = publishedRewardPeriod;
    }
  }

  async setInitialRewardItems(): Promise<void> {
    this.rewardItems = [];
    this.publishedRewardPeriod.rewardItems?.forEach((item: RewardItemDTO) => {
      this.rewardItems.push({
        id: item.id,
        prizeId: item.prize.id,
        name: item.prize.name,
        image: item.prize.image,
        description: item.prize.description,
        type: item.prize.type,
        cost: item.cost,
        quantity: item.quantity,
        redeemCount: item.redeemCount,
        maxRedeemPerUser: item.maxRedeemPerUser,
        userRedeemCount: this.getUserRedeemCountByRewardItemId(item.id),
        estimatedPrice: item.estimatedPrice || 0
      });
    });
  }

  getUserRedeemCountByRewardItemId(rewardItemId?: number): number {
    if (!rewardItemId) {
      return 0;
    }
    return this.exchangeRewardsHistory.filter(
      request =>
        request.rewardItem?.id === rewardItemId && request.status !== REWARD_REQUEST_STATUSES.REJECTED.label
    ).length;
  }

  async setInitialExchangeRewardHistory(): Promise<void> {
    const { data } = await fetchRewardRequestByUserId();
    this.exchangeRewardsHistory = sortArrayByDateField(data || [], 'updatedAt');
  }

  async setRedeemableDrops(): Promise<void> {
    const redeemableDrops = await fetchUserRedeemableDrops();
    this.redeemableDrops = redeemableDrops.data;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  async onRefetchData(): Promise<void> {
    await this.setRedeemableDrops();
    await this.setInitialExchangeRewardHistory();

    await this.setPublishedRewardPeriod();
    await this.setInitialRewardItems();
  }

  get typeRedeemedCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    if (!this.publishedRewardPeriod?.id) return counts;

    this.exchangeRewardsHistory.forEach(request => {
      if (
        request.periodRewardId === this.publishedRewardPeriod.id &&
        request.status !== REWARD_REQUEST_STATUSES.REJECTED.label
      ) {
        const type = request.rewardItem.prize.type;
        counts[type] = (counts[type] || 0) + 1;
      }
    });
    return counts;
  }

  get filteredRewards(): RewardDTO[] {
    let filteredData = this.rewardItems;
    if (this.selectedPrizeTypes.length) {
      filteredData = filteredData.filter(reward => this.selectedPrizeTypes.includes(reward.type));
    }
    if (this.isUnavailablePrizesHidden) {
      const settings = this.publishedRewardPeriod.settings || {};
      const typeCounts = this.typeRedeemedCounts;

      filteredData = filteredData.filter(
        reward => {
          const typeLimit = settings[reward.type];
          const typeCount = typeCounts[reward.type] || 0;
          const isTypeLimitReached = typeLimit !== undefined && typeLimit !== null && typeCount >= typeLimit;

          return reward.quantity > reward.redeemCount &&
            reward.cost <= this.redeemableDrops &&
            (!reward.maxRedeemPerUser || (reward.userRedeemCount || 0) < reward.maxRedeemPerUser) &&
            !isTypeLimitReached;
        }
      );
    }
    return filteredData;
  }

  get sortDesc(): boolean {
    return this.sortDirection === 1;
  }

  async setUserRewardRequestInfo(): Promise<void> {
    const { data } = await fetchCurrentUserRewardRequestInfo();
    this.userRewardRequestInfo = data;
  }

  get hasShippingInfo(): boolean {
    return !!this.userRewardRequestInfo;
  }

  onShippingInfoDialogConfirmed(newUserRewardRequestInfo: UserRewardRequestInfoDTO): void {
    this.userRewardRequestInfo = newUserRewardRequestInfo;
  }

  onShippingInfoDialogOpen(): void {
    this.shippingInfoDialogShown = true;
  }

  async created(): Promise<void> {
    this.currentUser = this.$store.getters['app/currentUser']?.id
      ? this.$store.getters['app/currentUser']
      : await this.$store.dispatch('app/fetchCurrentUser');

    await this.setRedeemableDrops();
    await this.setPublishedRewardPeriod();
    await this.setInitialExchangeRewardHistory();
    await this.setInitialRewardItems();

    this.setInitialPrizeTypesOption();
    this.setInitialSortByOptions();

    this.isFetchingRedeemable = false;
    this.isFetchingPrizes = false;
    this.isFetchingHistory = false;
  }
}
